import { useEffect, useState } from "react";
import { useReactFlow, Handle, Position, NodeResizer } from "@xyflow/react";
import { Select, Button, Input, Tooltip, Tag, Switch } from "antd";
import { NodeIndexOutlined, CloseOutlined, LinkOutlined, CopyOutlined } from "@ant-design/icons";
import api from "../../../../../../core/axios/axiosInstance";
import NodeNameInput from "./NodeNameInput";

const COLOR = "#7c3aed";

const WaFlowNode = ({ data, selected, id }) => {
  const { deleteElements } = useReactFlow();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/api/meta-flows")
      .then((r) => setFlows(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const selectedFlow = flows.find((f) => f.metaFlowId === data.metaFlowId || f._id === data.metaFlowDbId);

  const handleFlowSelect = (val) => {
    const flow = flows.find((f) => f._id === val);
    if (!flow) return;
    data.onChange?.({
      ...data,
      metaFlowDbId: flow._id,
      metaFlowId: flow.metaFlowId || "",
      flowName: flow.name,
      fieldMappings: flow.fieldMappings || [],
      isDraft: flow.metaStatus !== "PUBLISHED",
    });
  };

  const statusColor = {
    PUBLISHED: "green", DRAFT: "blue", LOCAL: "default",
    DEPRECATED: "orange", BLOCKED: "red", THROTTLED: "warning",
  };

  return (
    <div style={{
      background: "#fff",
      border: `2px solid ${selected ? COLOR : "#e5e7eb"}`,
      borderRadius: 10,
      padding: 14,
      minWidth: 280,
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      width: "100%",
      height: "100%",
      boxSizing: "border-box",
    }}>
      <NodeResizer minWidth={240} minHeight={80} isVisible={selected} color={COLOR} />
      <Handle type="target" position={Position.Left} style={{ background: COLOR }} />

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <NodeIndexOutlined style={{ color: COLOR, fontSize: 16 }} />
        <strong style={{ fontSize: 13 }}>WhatsApp Flow</strong>
        <Button
          type="text" size="small" icon={<CopyOutlined />}
          onClick={() => data.onDuplicate?.()}
          style={{ marginLeft: "auto", color: "#9ca3af" }}
        />
        <Button
          type="text" size="small" icon={<CloseOutlined />}
          onClick={() => deleteElements({ nodes: [{ id }] })}
          style={{ color: "#9ca3af" }}
        />
      </div>

      <div className="nodrag" onPointerDown={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <NodeNameInput data={data} />
        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Select Flow
        </label>
        <Select
          value={data.metaFlowDbId || undefined}
          onChange={handleFlowSelect}
          loading={loading}
          size="small"
          style={{ width: "100%", marginBottom: 6 }}
          placeholder="Choose a Meta Flow..."
          options={flows.map((f) => ({
            value: f._id,
            label: (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {f.name}
                <Tag color={statusColor[f.metaStatus] || "default"} style={{ fontSize: 9, padding: "0 4px" }}>
                  {f.metaStatus}
                </Tag>
              </span>
            ),
          }))}
        />

        {!loading && flows.length === 0 && (
          <div style={{
            padding: "6px 8px", background: "#fef3c7", borderRadius: 5,
            fontSize: 11, color: "#92400e", marginBottom: 8,
          }}>
            No flows yet.{" "}
            <a href="/whatsapp/meta-flows" target="_blank" style={{ color: COLOR, fontWeight: 600 }}>
              Create one here →
            </a>
          </div>
        )}

        <div style={{ marginBottom: 8 }}>
          <a
            href="/whatsapp/meta-flows"
            target="_blank"
            style={{ fontSize: 11, color: COLOR, display: "inline-flex", alignItems: "center", gap: 4 }}
          >
            <LinkOutlined /> {flows.length === 0 ? "Open Meta Flows page" : "Manage Flows"}
          </a>
        </div>

        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Header text (optional)
        </label>
        <Input
          value={data.headerText || ""}
          onChange={(e) => data.onChange?.({ ...data, headerText: e.target.value })}
          size="small"
          placeholder="e.g. Book Appointment"
          style={{ marginBottom: 8 }}
        />

        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Body text
        </label>
        <Input.TextArea
          value={data.bodyText || ""}
          onChange={(e) => data.onChange?.({ ...data, bodyText: e.target.value })}
          size="small"
          rows={2}
          placeholder="Please fill in the form below."
          style={{ marginBottom: 8 }}
        />

        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Footer text (optional)
        </label>
        <Input
          value={data.footerText || ""}
          onChange={(e) => data.onChange?.({ ...data, footerText: e.target.value })}
          size="small"
          placeholder="e.g. Takes less than 2 minutes"
          style={{ marginBottom: 8 }}
        />

        <label style={{ fontSize: 11, color: "#6b7280", display: "block", marginBottom: 4 }}>
          Button label (Open Form CTA)
        </label>
        <Input
          value={data.ctaLabel || ""}
          onChange={(e) => data.onChange?.({ ...data, ctaLabel: e.target.value })}
          size="small"
          placeholder="Open Form"
        />
      </div>

      {selectedFlow && (
        <div style={{
          marginTop: 10, padding: "6px 10px",
          background: `${COLOR}11`, borderRadius: 6,
          fontSize: 11, color: "#5b21b6",
        }}>
          <div><strong>{selectedFlow.name}</strong></div>
          {(selectedFlow.fieldMappings || []).length > 0 && (
            <div style={{ marginTop: 2 }}>
              Maps {selectedFlow.fieldMappings.length} field{selectedFlow.fieldMappings.length !== 1 ? "s" : ""} to contact
            </div>
          )}
          {selectedFlow.metaStatus !== "PUBLISHED" && (
            <div style={{ color: "#d97706", marginTop: 2 }}>
              ⚠ Flow is not published — publish it in Meta Flows to use live
            </div>
          )}

          {(selectedFlow.fieldMappings || []).length > 0 && (
            <Tooltip title="Pre-fill form fields with the contact's existing data (name, email, etc.) when the flow opens">
              <div
                style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 7, cursor: "pointer" }}
                onClick={() => data.onChange?.({ ...data, prefillEnabled: !data.prefillEnabled })}
              >
                <Switch
                  size="small"
                  checked={!!data.prefillEnabled}
                  onChange={(v) => data.onChange?.({ ...data, prefillEnabled: v })}
                  onClick={(_, e) => e.stopPropagation()}
                />
                <span style={{ fontSize: 11, fontWeight: 500, color: data.prefillEnabled ? "#7c3aed" : "#6b7280" }}>
                  Pre-fill with contact data
                </span>
              </div>
            </Tooltip>
          )}
        </div>
      )}

      <Handle type="source" position={Position.Right} style={{ background: COLOR }} />
    </div>
  );
};

export default WaFlowNode;
